import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchUsers,
  fetchUsersPaginated,
  fetchUsersAnalytics,
  createUser,
  fetchUserTokens,
  fetchUserTrackSearch,
  fetchUserTrackFilters,
  fetchLoginAnalytics,
  fetchLoginAnalyticsDetails,
  fetchUserAnalyticsDetails,
} from "./userAPI";
export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
    // you can still pass defaults here:
    // staleTime: 1000 , //* 60 * 5
    refetchOnWindowFocus: false,
  });
}

export function useUsersPaginated(options = {}) {
  const { page = 0, rowsPerPage = 10, searchTerm = "", filters = {}, sort = {} } = options;
  return useQuery({
    queryKey: ["usersPaginated", page, rowsPerPage, searchTerm, filters, sort],
    queryFn: () => fetchUsersPaginated({ page, limit: rowsPerPage, search: searchTerm, filters, sort }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
}

export function useUsersAnalytics(month = null, year = null) {
  return useQuery({
    queryKey: ["usersAnalytics", month, year],
    queryFn: () => fetchUsersAnalytics(month, year),
    refetchOnWindowFocus: false,
  });
}

export function useLoginAnalytics(month = null, year = null) {
  return useQuery({
    queryKey: ["loginAnalytics", month, year],
    queryFn: () => fetchLoginAnalytics(month, year),
    refetchOnWindowFocus: false,
  });
}

export function useLoginAnalyticsDetails(filters, options = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ["loginAnalyticsDetails", filters],
    queryFn: () => fetchLoginAnalyticsDetails(filters),
    refetchOnWindowFocus: false,
    enabled: enabled && !!filters.type && (!!filters.value || !!filters.year), // Only fetch if type and value are present (or value is 0 but valid, but usually string)
  });
}

export function useUserAnalyticsDetails(filters, options = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ["userAnalyticsDetails", filters],
    queryFn: () => fetchUserAnalyticsDetails(filters),
    refetchOnWindowFocus: false,
    enabled: enabled && !!filters.type && (!!filters.value || !!filters.year),
  });
}

export function useUserTokens(rowsPerPage = 10, search = "", filters = {}) {
  return useQuery({
    queryKey: ["usertokens", rowsPerPage, search, filters],
    queryFn: () => (search || Object.keys(filters).length > 0) ? fetchUserTrackSearch(search, filters, rowsPerPage) : fetchUserTokens(rowsPerPage),
    // you can still pass defaults here:
    // staleTime: 1000 , //* 60 * 5
    refetchOnWindowFocus: false,
  });
}

export function useUserTrackFilters() {
  return useQuery({
    queryKey: ["usertrackfilters"],
    queryFn: fetchUserTrackFilters,
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
}
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation(createUser, {
    onSuccess: () => qc.invalidateQueries(["users"]),
  });
}
